import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import adminApi from "../lib/adminApi";
import { BlockLogList } from "./BlockLogList";
import { HeldDuckRow } from "./HeldDuckRow";

const detail = (e, fallback) => e?.response?.data?.detail || fallback;

// Self-contained "Held ducks" panel (any Shopify staff member): ducks the view check holds, Waiting for review first,
// each with its override box, and the block log. Talks to /api/admin/held-ducks only, so it can move pages freely.
export const HeldDucksPanel = () => {
  const [data, setData] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await adminApi.get("/held-ducks", { params: outcome ? { outcome } : {} });
      setData(data);
    } catch (e) {
      setError(detail(e, "Could not load the held ducks. Please retry."));
    }
  }, [outcome]);

  useEffect(() => {
    load();
  }, [load]);

  const override = async (id, reason) => {
    setNotice("");
    try {
      const { data: out } = await adminApi.post(`/held-ducks/${id}/override`, { reason });
      setNotice(`Let through: ${Object.keys(out.views).join(", ")} (logged under ${out.by}).`);
      await load();
    } catch (e) {
      setError(detail(e, "Could not let that duck through. Please retry."));
    }
  };

  if (!data && !error) {
    return <p data-testid="held-ducks-loading" className="text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading held ducks…</p>;
  }
  const waiting = (data?.ducks || []).filter((d) => d.review).length;
  return (
    <div data-testid="held-ducks-panel" className="space-y-6">
      {data && !data.hold_on && (
        <p data-testid="held-ducks-hold-off" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          The hold is switched off (VIEW_CHECK_HOLD is not set): makers are not held yet. This list shows what would be held.
        </p>
      )}
      {error && <p data-testid="held-ducks-error" className="text-sm text-destructive">{error}</p>}
      {notice && <p data-testid="held-ducks-notice" className="text-sm text-emerald-700">{notice}</p>}
      <section className="space-y-3">
        <p data-testid="held-ducks-summary" className="text-sm text-muted-foreground">
          {data?.ducks?.length || 0} held · {waiting} waiting for review
        </p>
        {data?.ducks?.length ? (
          <ul className="space-y-4">
            {data.ducks.map((d) => <HeldDuckRow key={d.id} duck={d} onOverride={override} />)}
          </ul>
        ) : (
          <p data-testid="held-ducks-empty" className="rounded-3xl border border-dashed border-border p-6 text-sm text-muted-foreground">No duck is held right now.</p>
        )}
      </section>
      <BlockLogList log={data?.log} outcome={outcome} onOutcome={setOutcome} abandonDays={data?.abandon_days ?? 14} />
    </div>
  );
};
